import { useState } from "react";
import { Button, Select } from "risa-oasis-ui_v2";
import { BOOptionModel } from "../../../../../../data-model/boOptionModel";
import { ModalId } from "../../../../../../enums/modalId";
import BoOptionsModal from "../boOptionsModal/boOptionsModal";
import DraggableOptionCard from "../draggableOptionCard/draggableOptionCard";
import OptionsDeleteModal from "../optionsDeleteModal/optionsDeleteModal";

interface OrganizationOption {
  label: string;
  value: string;
}

interface BoOptionsSectionProps {
  sortedOptionsList: BOOptionModel[];
  optionsList: BOOptionModel[];
  draggedIndex: number | null;
  showModal: boolean;
  modalMode: "add" | "edit";
  editingOption: BOOptionModel | null;
  showDeleteModal: boolean;
  deletingOption: BOOptionModel | null;
  organizationOptions: OrganizationOption[];
  selectedOrgId: string;
  onAdd: () => void;
  onEdit: (option: BOOptionModel) => void;
  onDeleteClick: (option: BOOptionModel) => void;
  onModalClose: () => void;
  onSave: (option: BOOptionModel) => Promise<void>;
  onDeleteConfirm: () => Promise<void>;
  onDeleteCancel: () => void;
  onCopyFromOrg: (sourceOrgId: string) => Promise<void>;
  onDragStart: (e: React.DragEvent, index: number) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onDrop: (e: React.DragEvent, index: number) => void;
}

const BoOptionsSection = ({
  sortedOptionsList,
  optionsList,
  draggedIndex,
  showModal,
  modalMode,
  editingOption,
  showDeleteModal,
  deletingOption,
  organizationOptions,
  selectedOrgId,
  onAdd,
  onEdit,
  onDeleteClick,
  onModalClose,
  onSave,
  onDeleteConfirm,
  onDeleteCancel,
  onCopyFromOrg,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
}: BoOptionsSectionProps) => {
  const [selectedSourceOrg, setSelectedSourceOrg] = useState<string>("");
  const [isCopying, setIsCopying] = useState(false);

  const filteredOrgOptions = organizationOptions.filter(
    (org) => org.value !== selectedOrgId,
  );

  const handleCopyClick = async () => {
    if (!selectedSourceOrg) return;
    setIsCopying(true);
    try {
      await onCopyFromOrg(selectedSourceOrg);
      setSelectedSourceOrg("");
    } finally {
      setIsCopying(false);
    }
  };

  return (
    <>
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-700">BO Options</div>
          <Button
            disabled={false}
            children={"Add BO Option"}
            onClick={onAdd}
            buttonType={"primary"}
            size={"small"}
          />
        </div>
        <div className="flex flex-1 flex-col gap-2 overflow-auto">
          {sortedOptionsList.length === 0 ? (
            <div className="flex h-full flex-1 flex-col items-center justify-center gap-4">
              <div className="text-small text-primaryGray-6">
                No BO options found
              </div>
              {filteredOrgOptions.length > 0 && (
                <div className="flex flex-col items-center gap-3 rounded-md border border-primaryGray-15 bg-primaryGray-16 px-5 py-4">
                  <div className="text-tiny text-primaryGray-9">
                    Copy from another organization
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-52">
                      <Select
                        id="copyFromOrg"
                        options={filteredOrgOptions}
                        label=""
                        placeholder="Select Organization"
                        onOptionChange={(option: { value: string }) =>
                          setSelectedSourceOrg(option.value)
                        }
                        defaultValue={selectedSourceOrg}
                      />
                    </div>
                    <Button
                      disabled={!selectedSourceOrg || isCopying}
                      onClick={handleCopyClick}
                      buttonType="primary"
                      size="small"
                    >
                      {isCopying ? "Copying..." : "Copy"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            sortedOptionsList.map((option, index) => (
              <div
                key={option.value}
                draggable
                onDragStart={(e) => onDragStart(e, index)}
                onDragOver={onDragOver}
                onDragEnd={onDragEnd}
                onDrop={(e) => onDrop(e, index)}
              >
                <DraggableOptionCard
                  label={option.label}
                  value={option.value}
                  priority={option.priority}
                  isDragging={draggedIndex === index}
                  onEdit={() => onEdit(option)}
                  onDelete={() => onDeleteClick(option)}
                />
              </div>
            ))
          )}
        </div>
      </div>

      {showModal && (
        <BoOptionsModal
          data={editingOption}
          onModalClose={onModalClose}
          onSave={onSave}
          mode={modalMode}
          existingOptions={optionsList}
        />
      )}

      {showDeleteModal && deletingOption && (
        <OptionsDeleteModal
          modalId={ModalId.BO_OPTIONS_DELETE_MODAL}
          title="Delete BO Option"
          optionName={deletingOption.label}
          optionType="BO"
          onConfirm={onDeleteConfirm}
          onCancel={onDeleteCancel}
        />
      )}
    </>
  );
};

export default BoOptionsSection;
